import { useState } from "react";
import { DiscussionComponent } from "../../../components/atomic/discussionComponent";
import { Answer } from "../../../types/Rows";
import { CommentEditModal } from "./CommentEditModal";
import { cn } from "../../../lib/utils";

const pillBase =
  "flex items-center gap-1 px-2.5 py-1 rounded-[6px] text-[11px] font-medium cursor-pointer border transition-all select-none";

export const QuestionWithAnswer = ({
  index,
  input,
  comment,
  setComment,
  answer,
  isFollowUp,
  setAnswer,
  setFollowUp,
  depth = 0,
  reportId,
  saveAnswerImmediately,
}: {
  index: string;
  input: Answer;
  comment: string;
  setComment: (name: string) => void | Promise<void>;
  answer: string;
  setAnswer: (name: string) => void;
  isFollowUp: boolean;
  setFollowUp: (val: boolean) => void;
  depth: number;
  reportId?: string;
  saveAnswerImmediately?: (answer: Answer) => Promise<any>;
}) => {
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);

  const handleCommentSave = async (newComment: string) => {
    const result = setComment(newComment);
    if (result instanceof Promise) await result;
  };

  return (
    <div
      className={cn(
        "flex items-start gap-3 px-[18px] py-3.5 border-b border-[var(--sc-border)] last:border-b-0 hover:bg-[var(--sc-bg)] transition-colors",
        depth > 0 && "pl-[42px]"
      )}
    >
      {/* Number */}
      <span className="w-6 flex-shrink-0 text-[11px] font-medium text-[var(--sc-muted)] pt-0.5">
        {index}
      </span>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div
          className={cn(
            "text-[13px] text-[var(--sc-text)] mb-2 leading-[1.5]",
            input.isDirectAnswer && "font-medium"
          )}
        >
          {input.description}
          {input.isDirectAnswer && (
            <span className="ml-2 inline-flex px-1.5 py-0.5 text-[10px] font-semibold text-[#3570C4] bg-[#EDF3FC] rounded-[4px]">
              Bezpośredni
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Radio pills */}
          {!input.isBlank && (
            <div className="flex gap-1">
              <button
                onClick={() => setAnswer("yes")}
                className={cn(
                  pillBase,
                  answer === "yes"
                    ? "bg-[#E6F7F1] text-[#1A8055] border-[#2BAE72]"
                    : "bg-[var(--sc-surface)] text-[var(--sc-muted)] border-[var(--sc-border)] hover:border-[#C0C0BE]"
                )}
              >
                {answer === "yes" && "✓ "}Tak
              </button>
              <button
                onClick={() => setAnswer("no")}
                className={cn(
                  pillBase,
                  answer === "no"
                    ? "bg-[#FEE8E8] text-[#C03030] border-[#E05050]"
                    : "bg-[var(--sc-surface)] text-[var(--sc-muted)] border-[var(--sc-border)] hover:border-[#C0C0BE]"
                )}
              >
                {answer === "no" && "✗ "}Nie
              </button>
              <button
                onClick={() => setAnswer("na")}
                className={cn(
                  pillBase,
                  answer === "na"
                    ? "bg-[var(--sc-bg)] text-[var(--sc-muted)] border-[var(--sc-border)]"
                    : "bg-[var(--sc-surface)] text-[var(--sc-muted)] border-[var(--sc-border)] hover:border-[#C0C0BE]"
                )}
              >
                N/A
              </button>
            </div>
          )}

          {/* Inline comment */}
          <div
            className="flex-1 min-w-[160px] cursor-pointer rounded-[6px] border border-[var(--sc-border)] px-2.5 py-[5px] bg-[var(--sc-surface)] hover:border-[#99AECB] transition-colors"
            onClick={() => setIsCommentModalOpen(true)}
            title="Kliknij aby edytować komentarz"
          >
            {comment ? (
              <span className="text-[11px] text-[var(--sc-text)] line-clamp-1">{comment}</span>
            ) : (
              <span className="text-[11px] text-[var(--sc-muted)]">Komentarz...</span>
            )}
          </div>

          {/* Follow-up pill */}
          <button
            onClick={() => setFollowUp(!isFollowUp)}
            className={cn(
              "flex items-center gap-1 px-2 py-1 rounded-[20px] text-[10px] font-medium border cursor-pointer transition-all whitespace-nowrap flex-shrink-0",
              isFollowUp
                ? "bg-[#FEF3E2] text-[#C07810] border-[#F4A832]"
                : "bg-[var(--sc-bg)] text-[var(--sc-muted)] border-[var(--sc-border)] hover:border-[#C0C0BE]"
            )}
          >
            <span className="text-[9px]">{isFollowUp ? "⚑" : "+"}</span>
            Follow up
          </button>

          {/* Discussion */}
          {reportId && (
            <div className="flex-shrink-0">
              <DiscussionComponent resourceId={input.answerId} />
            </div>
          )}
        </div>
      </div>

      {/* Comment Edit Modal */}
      <CommentEditModal
        isOpen={isCommentModalOpen}
        initialComment={comment}
        onClose={() => setIsCommentModalOpen(false)}
        onSave={handleCommentSave}
        title={`Komentarz - ${index}`}
      />
    </div>
  );
};

// ===== LEGACY FALLBACK (unused, kept for reference) =====
const _RadioLegacy = ({ input, answer, setAnswer }: any) => (
  <div className="col-span-1 sm:col-span-2 flex flex-col sm:flex-row gap-1 p-1 justify-center">
    {!input.isBlank && (
      <>
        <div className="flex items-center justify-center">
          <label className="text-sm">
            <input
              defaultChecked={answer === "yes"}
              type="radio"
              onChange={(e: any) => setAnswer(e.target.value)}
              value="yes"
              name={"radio-" + input.questionId}
              className="mr-1"
            />
            Tak
          </label>
        </div>
        <div className="flex items-center justify-center">
          <label className="text-sm">
            <input
              defaultChecked={answer === "no"}
              type="radio"
              value="no"
              onChange={(e: any) => setAnswer(e.target.value)}
              name={"radio-" + input.questionId}
              className="mr-1"
            />
            Nie
          </label>
        </div>
        <div className="flex items-center justify-center">
          <label className="text-sm">
            <input
              defaultChecked={answer === "na"}
              type="radio"
              value="na"
              onChange={(e: any) => setAnswer(e.target.value)}
              name={"radio-" + input.questionId}
              className="mr-1"
            />
            N/A
          </label>
        </div>
      </>
    )}
  </div>
);
